import { AcceptedMediaTypesHint } from "@/components/media/AcceptedMediaTypesHint";
import { isAcceptedVideoFile, VIDEO_DROPZONE_ACCEPT } from "@/lib/media/video";
import { fileIdentityKey } from "@/lib/utils";
import { CloudUpload, Film } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TranslatedFormMessage } from "../ui/custom-form";
import { FileDropzone, FileItem } from "../ui/FileDropzone";
import { FormField, FormItem } from "../ui/form";
import { Thumbnail } from "../ui/tumbnail";
import { ACCEPTED_IMAGE_TYPES, isVideoFile } from "./DragAndDropLoadMedia";
import {
  formatMaxUploadSize,
  IMAGE_MAX_SIZE_MB,
  VIDEO_MAX_SIZE_MB,
} from "@/lib/media/upload-limits";
import { LoadImageDropzoneCta } from "./LoadImageDropzoneCta";
import { FormFieldProps } from "./types";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

const IMAGE_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
};

function isAcceptedMediaFile(file: File, allowVideo: boolean): boolean {
  if ((ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return true;
  }
  if (allowVideo && isAcceptedVideoFile(file)) {
    return true;
  }
  return false;
}

function mergeFiles(existing: File[], incoming: File[]): File[] {
  const seen = new Set(existing.map(fileIdentityKey));
  const merged = [...existing];
  for (const file of incoming) {
    const key = fileIdentityKey(file);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(file);
  }
  return merged;
}

const DragAndDropLoadMultipleImages = ({
  control,
  config,
  className,
  getValues: _getValues,
  allowVideo = true,
}: FormFieldProps & { allowVideo?: boolean }) => {
  const { t } = useTranslation();
  const vt = useVisibleT(); 
  
  const accept = allowVideo
    ? { ...IMAGE_ACCEPT, ...VIDEO_DROPZONE_ACCEPT }
    : IMAGE_ACCEPT;

  return (
    <FormField
      control={control}
      name={config.name}
      render={({ field }) => {
        const files: File[] = Array.isArray(field.value) ? field.value : [];

        return (
          <FormItem className="flex flex-col gap-3">
            <FileDropzone
              multiple
              accept={accept}
              onDrop={(incoming: File[]) => {
                const valid = incoming.filter((f) =>
                  isAcceptedMediaFile(f, allowVideo),
                );
                if (valid.length > 0) {
                  field.onChange(mergeFiles(files, valid));
                }
              }}
              className={className}
            >
              {() => (
                <div className="flex flex-col items-center justify-center gap-6">
                  <Thumbnail className="bg-brand-50">
                    <CloudUpload className="text-gray-700 size-6" />
                  </Thumbnail>
                  <LoadImageDropzoneCta dragCtaKey="loadImage.dragCtaMultiple" />
                  <AcceptedMediaTypesHint showVideo={allowVideo} />
                </div>
              )}
            </FileDropzone>

            {files.length > 0 && (
              <p className="text-sm text-gray-600">
                {files.length} {t("mediaLibrary.batch.filesSelected")}
              </p>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2 text-xs text-gray-500">
                <span>
                  {vt(
                    "mediaLibrary.upload.maxImageSize",
                    `Taille maximale image : ${formatMaxUploadSize(IMAGE_MAX_SIZE_MB)}`
                  )}
                </span>
                {allowVideo && (
                  <>
                    <span className="mx-1">·</span>
                    <span>
                      {vt(
                        "mediaLibrary.upload.maxVideoSize",
                        `vidéo : ${formatMaxUploadSize(VIDEO_MAX_SIZE_MB)}`
                      )}
                    </span>
                  </>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {vt("mediaLibrary.batch.orderHint", "L'ordre des fichiers déterminera l'ordre d'import.")}
              </span>
            </div>
       
       

            {files.length > 0 && (
              <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto">
                {files.map((file, index) => (
                  <FileItem
                    key={`${fileIdentityKey(file)}-${index}`}
                    file={file}
                    image={
                      isVideoFile(file) ? (
                        <Film className="w-6 h-6 text-gray-500" />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          className="w-10 h-10 object-cover rounded"
                        />
                      )
                    }
                    onDelete={() =>
                      field.onChange(
                        files.filter(
                          (f) => fileIdentityKey(f) !== fileIdentityKey(file),
                        ),
                      )
                    }
                    className={className}
                  />
                ))}
              </div>
            )}
            <TranslatedFormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default DragAndDropLoadMultipleImages;
